<template>
  <div class="room-structure-container">
    <el-row :gutter="16">
      <el-col :span="4">
        <div class="ds-side-card">
          <div class="ds-side-card__title">{{ $t('roomStructure.structureTitle') }}</div>
          <floor-unit-room-shop-tree ref="floorUnitRoomShopTree" @switchFloorUnit="switchFloorUnit" />
        </div>
      </el-col>
      <el-col :span="20">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('roomStructure.page.title')" :subtitle="$t('roomStructure.page.subtitle')" />

        <!-- 状态图例：颜色仅作辅助，始终配文字说明 -->
        <div class="ds-legend">
          <span class="ds-legend__title">{{ $t('roomStructure.legend.title') }}</span>
          <span class="ds-legend__item ds-legend__item--danger">
            <i class="ds-legend__dot"></i>{{ $t('roomStructure.legend.debt') }}
          </span>
          <span class="ds-legend__item ds-legend__item--success">
            <i class="ds-legend__dot"></i>{{ $t('roomStructure.legend.noOwner') }}
          </span>
          <span class="ds-legend__item ds-legend__item--primary">
            <i class="ds-legend__dot"></i>{{ $t('roomStructure.legend.occupied') }}
          </span>
          <span class="ds-legend__item ds-legend__item--info">
            <i class="ds-legend__dot"></i>{{ $t('roomStructure.legend.handOver') }}
          </span>
          <span class="ds-legend__item ds-legend__item--muted">
            <i class="ds-legend__dot"></i>{{ $t('roomStructure.legend.other') }}
          </span>
        </div>

        <div class="ds-panel">
          <ds-toolbar :total="roomStructureInfo.rooms.length" :show-column-setting="false" :show-density="false"
            :show-refresh="true" @refresh="reloadCurrent">
            <template #left>
              <span class="ds-panel__title">{{ $t('roomStructure.page.title') }}</span>
            </template>
          </ds-toolbar>

          <div class="ds-panel__body">
            <!-- 单层（房间数 < 5） -->
            <div v-if="roomStructureInfo.layerRoomCount < 5" class="ds-room-grid">
              <div v-for="(room, index) in roomStructureInfo.rooms" :key="index" class="ds-room-grid__item">
                <div class="ds-room-card" :class="getRoomClass(room)" @dblclick="toSimplifyAcceptance(room)">
                  <div class="ds-room-card__number ds-num">
                    {{ room.floorNum }}-{{ room.unitNum }}-{{ room.roomNum }}
                  </div>
                  <div class="ds-room-card__state">{{ room.stateName }}</div>
                  <div class="ds-room-card__owner">{{ room.ownerName || $t('roomStructure.noOwner') }}</div>
                  <div class="ds-room-card__debt ds-num">
                    {{ $t('roomStructure.owe') }}：{{ formatMoney(room.oweAmount, { symbol: '' }) }}
                    {{ $t('roomStructure.yuan') }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 多层（园区类，按楼层分组） -->
            <div v-else>
              <div v-for="(val, key, index) in roomStructureInfo.parkRooms" :key="index">
                <div class="ds-floor-title">{{ key }}F</div>
                <div class="ds-room-grid">
                  <div v-for="(room, index) in val" :key="index" class="ds-room-grid__item">
                    <div class="ds-room-card" :class="getRoomClass(room)" @dblclick="toSimplifyAcceptance(room)">
                      <div class="ds-room-card__number ds-num">
                        {{ room.floorNum }}-{{ room.unitNum }}-{{ room.roomNum }}
                      </div>
                      <div class="ds-room-card__state">{{ room.stateName }}</div>
                      <div class="ds-room-card__owner">{{ room.ownerName || $t('roomStructure.noOwner') }}</div>
                      <div class="ds-room-card__debt ds-num">
                        {{ $t('roomStructure.owe') }}：{{ formatMoney(room.oweAmount, { symbol: '' }) }}
                        {{ $t('roomStructure.yuan') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import floorUnitRoomShopTree from '@/components/room/floorUnitRoomShopTree'
import { listRoomStructure } from '@/api/room/roomStructureApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'

export default {
  name: 'RoomStructureList',
  components: {
    floorUnitRoomShopTree,
    DsPageHeader,
    DsToolbar
  },
  data() {
    return {
      roomStructureInfo: {
        rooms: [],
        parkRooms: {},
        layerRoomCount: 4
      },
      communityId: '',
      // 当前选中的单元，供「刷新」按钮重新加载
      currentUnitId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    setTimeout(() => {
      this.$refs.floorUnitRoomShopTree.selectFirstUnit()
    }, 1500)
  },
  methods: {
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value, options) {
      return formatMoney(value, options)
    },
    handleSwitchUnit(params) {
      this.loadRooms(params.unitId)
    },
    switchFloorUnit(data) {
      if (!data.unitId) {
        return
      }
      this.loadRooms(data.unitId)
    },
    reloadCurrent() {
      if (this.currentUnitId) {
        this.loadRooms(this.currentUnitId)
      }
    },
    async loadRooms(unitId) {
      this.currentUnitId = unitId
      this.roomStructureInfo.rooms = []
      this.roomStructureInfo.parkRooms = {}

      try {
        const params = {
          page: 1,
          row: 100,
          unitId: unitId,
          communityId: this.communityId
        }

        const { data } = await listRoomStructure(params)
        this.roomStructureInfo.rooms = Array.isArray(data) ? data : []
        this.supportPark()
      } catch (error) {
        console.error('Failed to load rooms:', error)
      }
    },
    /**
     * 房间卡片语义样式：颜色仅作辅助，卡片内始终有状态文字与欠费金额。
     * 判定口径与改造前完全一致（欠费 > 0、无业主、房屋状态码）。
     */
    getRoomClass(room) {
      if (!room) return 'ds-room-card--muted'
      if (room.oweAmount > 0) {
        return 'ds-room-card--danger'
      }
      if (!room.ownerName) {
        return 'ds-room-card--success'
      }
      if (room.state === '2001' || room.state === '2006') {
        return 'ds-room-card--primary'
      }
      if (room.state === '2003') {
        return 'ds-room-card--info'
      }
      return 'ds-room-card--muted'
    },
    toSimplifyAcceptance(room) {
      this.$router.push('/pages/property/simplifyAcceptance?tab=业务受理&searchType=1&searchValue=' + `${room.floorNum}-${room.unitNum}-${room.roomNum}`)
    },
    supportPark() {
      const parkRooms = this.roomStructureInfo.parkRooms
      if (!this.roomStructureInfo.rooms || this.roomStructureInfo.rooms.length < 1) {
        return
      }

      this.roomStructureInfo.rooms.forEach(item => {
        if (!parkRooms[item.layer]) {
          parkRooms[item.layer] = []
        }
        parkRooms[item.layer].push(item)
      })

      this.roomStructureInfo.parkRooms = parkRooms
      if (Object.keys(parkRooms).length > 0) {
        this.roomStructureInfo.layerRoomCount = parkRooms[Object.keys(parkRooms)[0]].length
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.room-structure-container {
  padding: var(--ds-spacing-lg);
  min-height: calc(100vh - 60px);
  background-color: var(--ds-gray-100);
}

/* 左侧树卡片 */
.ds-side-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-300);
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__body {
    padding: var(--ds-spacing-lg);
  }
}

/* 状态图例 */
.ds-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--ds-spacing-md);
  padding: var(--ds-spacing-sm) var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-md);
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);

  &__title {
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
  }

  &__item {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-spacing-xs);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-600);

    .ds-legend__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: currentColor;
    }

    &--danger {
      color: var(--ds-danger);
    }

    &--success {
      color: var(--ds-success);
    }

    &--primary {
      color: var(--ds-primary);
    }

    &--info {
      color: var(--ds-info);
    }

    &--muted {
      color: var(--ds-gray-500);
    }
  }
}

/* 房间网格 */
.ds-room-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--ds-spacing-md);

  &__item {
    min-width: 0;
  }
}

.ds-floor-title {
  font-size: var(--ds-font-size-md);
  font-weight: 600;
  color: var(--ds-gray-800);
  padding: var(--ds-spacing-sm) 0;
  border-bottom: 1px solid var(--ds-gray-200);
  margin-bottom: var(--ds-spacing-md);
}

/* 房间卡片：浅底 + 左侧语义色条，保证文字可读（对比度达标） */
.ds-room-card {
  position: relative;
  padding: var(--ds-spacing-sm) var(--ds-spacing-sm) var(--ds-spacing-sm) var(--ds-spacing-md);
  border-radius: var(--ds-radius-sm);
  border: 1px solid var(--ds-gray-300);
  border-left: 3px solid var(--ds-gray-400);
  background: #fff;
  cursor: pointer;
  transition: all var(--ds-transition-normal);
  height: 118px;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--ds-shadow-md);
  }

  &__number {
    font-weight: 600;
    color: var(--ds-gray-800);
    margin-bottom: var(--ds-spacing-xs);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__state {
    font-size: var(--ds-font-size-xs);
    font-weight: 600;
    margin-bottom: 3px;
  }

  &__owner {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-600);
    margin-bottom: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__debt {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-600);
  }

  /* 欠费：危险色 */
  &--danger {
    border-left-color: var(--ds-danger);
    background: rgba(var(--ds-danger-rgb), 0.06);

    .ds-room-card__state {
      color: var(--ds-danger);
    }

    .ds-room-card__debt {
      color: var(--ds-danger);
      font-weight: 600;
    }
  }

  /* 无业主：成功色 */
  &--success {
    border-left-color: var(--ds-success);
    background: rgba(var(--ds-success-rgb), 0.06);

    .ds-room-card__state {
      color: var(--ds-success);
    }
  }

  /* 已入住/已出租：主色 */
  &--primary {
    border-left-color: var(--ds-primary);
    background: rgba(var(--ds-primary-rgb), 0.06);

    .ds-room-card__state {
      color: var(--ds-primary);
    }
  }

  /* 已交房：信息色 */
  &--info {
    border-left-color: var(--ds-info);
    background: rgba(var(--ds-info-rgb), 0.06);

    .ds-room-card__state {
      color: var(--ds-info);
    }
  }

  /* 其他状态：中性色 */
  &--muted {
    border-left-color: var(--ds-gray-400);
    background: var(--ds-gray-50);

    .ds-room-card__state {
      color: var(--ds-gray-700);
    }
  }
}
</style>